import { useEffect, useState, useCallback, Fragment } from "react";
import { Wallet, Calendar, Download, ChevronDown, ChevronRight, Users, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Default: del 1 al fin del mes en curso
function defaultRange() {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { from: fmt(from), to: fmt(to) };
}

const EMP_TYPE_LABELS = {
    planta: { label: "Planta / Nómina", color: "fm-badge-positive" },
    eventual: { label: "Eventual", color: "fm-badge-warning" },
    por_hora: { label: "Por hora", color: "fm-badge-warning" },
    por_guardia: { label: "Por guardia", color: "fm-badge-warning" },
    freelance: { label: "Freelance", color: "fm-badge-warning" },
};

export default function Payroll() {
    const { user } = useAuth();
    const isAuthorized = user?.role === "superadmin" || user?.role === "direccion";
    const def = defaultRange();
    const [from, setFrom] = useState(def.from);
    const [to, setTo] = useState(def.to);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});

    const load = useCallback(async () => {
        if (!isAuthorized) return;
        setLoading(true);
        try {
            const { data: res } = await api.get("/reports/payroll", { params: { from_date: from, to_date: to } });
            setData(res);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al cargar nómina");
        } finally {
            setLoading(false);
        }
    }, [from, to, isAuthorized]);

    useEffect(() => { load(); }, [load]);

    const exportCSV = () => {
        if (!data?.rows?.length) return;
        const headers = ["Nombre", "Puesto", "Esquema", "Base", "Concepto", "Tarifa", "Unidades", "Subtotal", "Periodicidad", "Notas"];
        const rows = [headers.join(",")];
        for (const r of data.rows) {
            if (r.breakdown.length === 0) {
                rows.push([`"${r.name}"`, r.position || "", r.employment_type, `"${r.base || ""}"`, "", "", "", "0", r.pay_period || "", `"${(r.payroll_notes || "").replace(/"/g, "")}"`].join(","));
            }
            r.breakdown.forEach((b) => {
                rows.push([`"${r.name}"`, r.position || "", r.employment_type, `"${r.base || ""}"`, `"${b.concept}"`, b.rate, b.units, b.amount, r.pay_period || "", `"${(r.payroll_notes || "").replace(/"/g, "")}"`].join(","));
            });
        }
        rows.push("");
        rows.push(`TOTAL,,,,,,,${data.grand_total}`);
        const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `FindMed-Nomina-${from}_${to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV descargado");
    };

    if (!isAuthorized) {
        return (
            <div className="p-6">
                <div className="fm-card p-8 text-center text-slate-600">
                    🔒 Solo superadmin / dirección tienen acceso a este reporte.
                </div>
            </div>
        );
    }

    const totalsByType = (data?.rows || []).reduce((acc, r) => {
        acc[r.employment_type] = (acc[r.employment_type] || 0) + r.total;
        return acc;
    }, {});

    return (
        <div className="p-6 space-y-5" data-testid="payroll-page">
            {/* Header */}
            <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    <Wallet className="w-4 h-4" /> Nómina del periodo
                </div>
                <h1 className="font-display font-bold text-3xl mt-1">Lo que se debe pagar</h1>
                <p className="text-sm text-slate-500 mt-1">
                    Calcula automáticamente la nómina por persona según su esquema de contratación, los turnos registrados en asistencia y los servicios cubiertos en el periodo.
                </p>
            </div>

            {/* Controls */}
            <div className="fm-card p-4 flex items-end gap-3 flex-wrap">
                <div>
                    <label className="fm-label">Desde</label>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="fm-input" data-testid="payroll-from" />
                </div>
                <div>
                    <label className="fm-label">Hasta</label>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="fm-input" data-testid="payroll-to" />
                </div>
                <button onClick={load} disabled={loading} className="fm-btn-primary" data-testid="payroll-load">
                    <Calendar className="w-3.5 h-3.5" /> {loading ? "Calculando..." : "Calcular nómina"}
                </button>
                {data?.rows?.length > 0 && (
                    <button onClick={exportCSV} className="fm-btn-outline ml-auto" data-testid="payroll-export-csv">
                        <Download className="w-3.5 h-3.5" /> Exportar CSV
                    </button>
                )}
            </div>

            {/* KPIs */}
            {data && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total del periodo</div>
                        <div className="font-mono font-bold text-2xl text-emerald-700 mt-1" data-testid="payroll-grand-total">{money(data.grand_total)}</div>
                        <div className="text-[10px] text-slate-500 mt-1">{data.days_in_period} día(s) calendario</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Personas a pagar</div>
                        <div className="font-bold text-2xl mt-1">{data.count}</div>
                        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> con compensación calculada</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Planta / nómina</div>
                        <div className="font-mono font-bold text-lg mt-1">{money(totalsByType.planta || 0)}</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Eventuales / hora / guardia</div>
                        <div className="font-mono font-bold text-lg mt-1">
                            {money((totalsByType.eventual || 0) + (totalsByType.por_hora || 0) + (totalsByType.por_guardia || 0) + (totalsByType.freelance || 0))}
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["", "Persona", "Puesto", "Esquema", "Periodicidad", "Conceptos", "Total"].map((h) => (
                            <th key={h} className={`text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider ${h === "Total" ? "text-right" : ""}`}>{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {(data?.rows || []).map((r) => {
                            const typeLbl = EMP_TYPE_LABELS[r.employment_type] || { label: r.employment_type, color: "fm-badge-neutral" };
                            const isOpen = expanded[r.personnel_id];
                            return (
                                <Fragment key={r.personnel_id}>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payroll-row-${r.personnel_id}`}>
                                        <td className="py-2 px-3">
                                            <button
                                                onClick={() => setExpanded({ ...expanded, [r.personnel_id]: !isOpen })}
                                                className="text-slate-500 hover:text-slate-900"
                                                title={isOpen ? "Ocultar desglose" : "Ver desglose"}
                                            >
                                                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                            </button>
                                        </td>
                                        <td className="py-2 px-3 font-medium">
                                            {r.name}
                                            {r.base && <div className="text-[10px] text-slate-500">{r.base}</div>}
                                            {r.payroll_notes && <div className="text-[10px] text-amber-700 italic mt-0.5">📝 {r.payroll_notes}</div>}
                                        </td>
                                        <td className="py-2 px-3 capitalize text-xs">{r.position}</td>
                                        <td className="py-2 px-3"><span className={`fm-badge ${typeLbl.color}`}>{typeLbl.label}</span></td>
                                        <td className="py-2 px-3 text-xs capitalize">{r.pay_period?.replace("_", " ")}</td>
                                        <td className="py-2 px-3 text-xs text-slate-600">
                                            {r.breakdown.length === 0 ? <span className="italic text-slate-400">sin conceptos en el periodo</span> : `${r.breakdown.length} concepto(s)`}
                                        </td>
                                        <td className="py-2 px-3 font-mono text-right font-bold" data-testid={`payroll-total-${r.personnel_id}`}>
                                            <div>{money(r.total)}</div>
                                            <a
                                                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/pdf/payroll-receipt/${r.personnel_id}?from_date=${from}&to_date=${to}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded-sm normal-case"
                                                data-testid={`payroll-receipt-${r.personnel_id}`}
                                                title="Descarga el recibo PDF firmable de esta persona"
                                            >
                                                <FileText className="w-2.5 h-2.5" /> Recibo PDF
                                            </a>
                                        </td>
                                    </tr>
                                    {isOpen && r.breakdown.length > 0 && (
                                        <tr className="bg-slate-50/60">
                                            <td colSpan="7" className="px-12 py-3">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="text-[9px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                                            <th className="text-left py-1">Concepto</th>
                                                            <th className="text-right py-1">Tarifa</th>
                                                            <th className="text-right py-1">Unidades</th>
                                                            <th className="text-right py-1">Subtotal</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {r.breakdown.map((b, i) => (
                                                            <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                                                <td className="py-1">{b.concept}</td>
                                                                <td className="py-1 text-right font-mono">{money(b.rate)}</td>
                                                                <td className="py-1 text-right font-mono">{b.units}</td>
                                                                <td className="py-1 text-right font-mono font-bold">{money(b.amount)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                        {(!data || data.rows.length === 0) && !loading && (
                            <tr><td colSpan="7" className="py-10 text-center text-slate-400 italic">Sin datos en el periodo seleccionado.</td></tr>
                        )}
                    </tbody>
                    {data?.rows?.length > 0 && (
                        <tfoot>
                            <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                                <td colSpan="6" className="py-3 px-3 text-right">TOTAL DEL PERIODO</td>
                                <td className="py-3 px-3 text-right font-mono text-emerald-700">{money(data.grand_total)}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            <div className="text-[10px] text-slate-500 leading-relaxed">
                <strong>Cómo se calcula:</strong> Planta = (sueldo/30) × días. Por hora = horas registradas en Asistencia × $/h. Por guardia = turnos cerrados en Asistencia (≥18h → guardia 24h). Eventual/Freelance = servicios cubiertos × $/evento + horas + bono. Solo se incluyen turnos con check-out registrado.
            </div>
        </div>
    );
}
